import { useEffect } from 'react';
import { Link } from 'react-router-dom';

export function MentalControlCategory() {
  useEffect(() => {
    // Set page title and meta description
    document.title = "Mental Control for High-Functioning Minds";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 
        "Structured thinking frameworks to stop overthinking, set mental boundaries, and control cognitive overload. Built for analytical, high-performing adults."
      );
    }
  }, []);

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 py-6 px-6">
        <div className="max-w-4xl mx-auto">
          <Link to="/" className="text-[#2C5F7D] hover:text-[#234a5f] transition-colors">
            ← Back to Home
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          {/* H1 Title */}
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8">
            Mental Control for High-Functioning Minds
          </h1>

          {/* Category Introduction - 250-350 words, SEO optimized */}
          <div className="prose prose-lg max-w-none mb-12">
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              Mental control is not about silencing your mind. It's about directing mental energy toward productive outcomes instead of letting it cycle through unproductive loops. For high-functioning adults, overthinking often stems from excess cognitive capacity without clear boundaries—not from dysfunction.
            </p>

            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              A mental control system provides structure for analytical thinkers who experience decision fatigue, rumination, and cognitive overload. Unlike reactive coping strategies, structured thinking frameworks interrupt mental loops before they drain your energy. The difference between rumination and reflection is simple: reflection moves you forward; rumination keeps you stuck.
            </p>

            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              This approach targets the root cause of overthinking: the absence of mental boundaries. When your mind lacks clear stopping points, thinking continues indefinitely. You replay conversations, re-evaluate completed decisions, and analyze problems you cannot act on. The solution is not more willpower—it's a repeatable system that defines when thinking starts and when it stops.
            </p>

            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Mental control frameworks are designed for professionals who think in systems, not those seeking emotional reassurance. If you experience persistent mental activity, decision paralysis, or nighttime thinking spirals, structured cognitive boundaries can redirect that energy into usable output.
            </p>

            <div className="space-y-3 my-8 pl-6 border-l-4 border-[#2C5F7D]">
              <p className="text-base text-[#2C5F7D]">
                [Read: The 3×3×3 Method to Stop Overthinking Before It Escalates]
              </p>
              <p className="text-base text-[#2C5F7D]">
                [Read: How to Set Mental Boundaries for Decision-Heavy Work]
              </p>
              <p className="text-base text-[#2C5F7D]">
                [Read: Why Mental Loops Repeat and How to Interrupt Them]
              </p>
            </div>
          </div>

          {/* Philosophy Section */}
          <section className="bg-gray-50 rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Control {'>'} Calm
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              The goal is not to quiet your mind. The goal is to control where your mental energy goes. Calm is a byproduct of control, not the method. High-functioning minds do not need to be turned off—they need clear direction, boundaries, and systems that prevent waste. Control gives you agency. Calm is what follows when you stop fighting your own thinking patterns.
            </p>
          </section>

          {/* Semantic Definitions for LLM Indexing */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Key Concepts
            </h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Overthinking</h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  Excessive mental processing that continues beyond the point of productive analysis. Overthinking occurs when the mind replays scenarios, evaluates completed decisions, or analyzes problems without new information or actionable outcomes.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Rumination</h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  Repetitive, circular thinking about past events or decisions without forward movement. Unlike reflection, which extracts insight and closes, rumination loops without resolution. It is mental activity that consumes energy but produces no usable output.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Mental Loops</h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  Recurring thought patterns that replay without reaching closure. Mental loops are self-reinforcing cycles where the same concerns, conversations, or decisions are re-evaluated repeatedly, often triggered by incomplete cognitive processing or absence of clear stopping criteria.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Cognitive Boundaries</h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  Defined limits that determine when mental processing should begin and when it should stop. Cognitive boundaries prevent overthinking by establishing clear endpoints for analysis, decision-making, and problem-solving. They function as mental containment structures that redirect thinking before it becomes unproductive.
                </p>
              </div>
            </div>
          </section>

          {/* FAQ Section - Schema-Friendly */}
          <section className="bg-white border border-gray-200 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Frequently Asked Questions
            </h2>
            
            <div className="space-y-6" itemScope itemType="https://schema.org/FAQPage">
              <div itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                <h3 className="text-lg font-bold text-gray-900 mb-2" itemProp="name">
                  What is the difference between overthinking and normal thinking?
                </h3>
                <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                  <p className="text-base text-gray-700 leading-relaxed" itemProp="text">
                    Normal thinking moves toward resolution. Overthinking repeats without progress. If you are analyzing the same scenario multiple times without new information, you are overthinking. Normal thinking extracts what is useful and stops. Overthinking continues because there is no defined stopping point.
                  </p>
                </div>
              </div>

              <div itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                <h3 className="text-lg font-bold text-gray-900 mb-2" itemProp="name">
                  Can mental control systems work for analytical thinkers?
                </h3>
                <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                  <p className="text-base text-gray-700 leading-relaxed" itemProp="text">
                    Yes. Mental control systems are designed specifically for analytical minds. They provide structure, not suppression. Instead of fighting your thinking patterns, you redirect them using repeatable frameworks. This approach respects your cognitive style while preventing mental waste.
                  </p>
                </div>
              </div>

              <div itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                <h3 className="text-lg font-bold text-gray-900 mb-2" itemProp="name">
                  How long does it take to implement cognitive boundaries?
                </h3>
                <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                  <p className="text-base text-gray-700 leading-relaxed" itemProp="text">
                    Cognitive boundaries can be applied immediately. The 3×3×3 Method, for example, takes less than 10 minutes to implement the first time. Results are not instant—boundaries must be reinforced through repetition—but the framework itself is simple enough to use the same day you learn it.
                  </p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-12 px-6 bg-gray-50 border-t border-gray-200 mt-16">
        <div className="max-w-4xl mx-auto text-center text-gray-600">
          <p className="mb-2">The Overthinking Reset</p>
          <p className="text-sm">© 2024. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
